import { usePrevious, v2_useCart } from '@crocodille/common';
import { isSome } from '@crocodille/util';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useRef } from 'react';

import CartIcon from '../../icons/cart-icon';
import styles from './cart-status.module.scss';
import { formatCurrency } from '@utils/format';
import { useRouter } from '@framework/router';
import AppLayoutHeaderDivider from '@layouts/app-layout/app-layout-header-divider';

export interface CartStatusProps {
  open: boolean;
  onPress(): void;
}

const SHAKE_ANIMATION: Keyframe[] = [
  { transform: `rotate(0deg) scale(1)` },
  { transform: `rotate(5deg) scale(1.15)` },
  { transform: `rotate(0deg) scale(1.3)` },
  { transform: `rotate(-5deg) scale(1.15)` },
  { transform: `rotate(0deg) scale(1)` },
];

const SCALE_ANIMATION: Keyframe[] = [
  { transform: `scale(1)` },
  { transform: `scale(1.5)` },
  { transform: `scale(1)` },
];

const CartStatus: React.FC<React.PropsWithChildren<CartStatusProps>> = ({
  onPress,
  open,
}) => {
  const { t } = useTranslation();
  const { data: order } = v2_useCart();
  const { locale } = useRouter();

  const itemCount = useMemo(
    () => order?.items?.reduce?.((acc, item) => acc + item.quantity, 0) ?? 0,
    [order?.items],
  );

  const prevItemCount = usePrevious(itemCount);

  const iconRef = useRef<SVGSVGElement>(null);
  const countRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (isSome(prevItemCount)) {
      iconRef.current?.animate?.(SHAKE_ANIMATION, {
        duration: 450,
        easing: 'ease-in-out',
        iterations: 1,
      });

      countRef.current?.animate?.(SCALE_ANIMATION, {
        duration: 400,
        easing: 'ease-in-out',
        iterations: 1,
        delay: 50,
      });
    }
  }, [prevItemCount]);

  return (
    <>
      <button
        className={cn(styles.container, { [styles.isOpen]: open })}
        type="button"
        onClick={onPress}
        title={t('cart.items_in_cart', { count: itemCount })}
      >
        <CartIcon ref={iconRef} className={styles.icon} />
        {itemCount > 0 ? (
          <span ref={countRef} className={styles.itemCount}>
            {itemCount}
          </span>
        ) : null}
        <span className={styles.price}>
          {formatCurrency(
            order?.total ?? 0,
            order?.currencyCode ?? 'CZK',
            locale,
          )}
        </span>
      </button>
      <AppLayoutHeaderDivider className={styles.divider} />
    </>
  );
};

export default CartStatus;
